import { useMutation } from '@connectrpc/connect-query'
import { updateSingularDay } from '@ebalance/proto/bffuser/v1/singular_day_service-SingularDayService_connectquery'
import type { ListSingularDaysResponse } from '@ebalance/proto/bffuser/v1/singular_day_service_pb'
import { useQueryClient } from '@tanstack/react-query'

import { type InvalidatingMutationResultFor } from '~/hooks/useInvalidatingMutation'
import { singularDayQueryKeys } from '~/pages/AdminSingularDay/hooks/queryKeys'

/**
 * 特異日更新 mutation hook。
 *
 * identity は surrogate `id` (UUID)。 date は immutable だが一意ではなく
 * (全エリア共通と特定エリアが同一日付に併存しうる) identity には使えないため、
 * 更新後の一覧は「同一 id の行を response の更新後 row で置換する」 だけで
 * 確定できる。 そのため invalidate + 再取得ではなく `setQueriesData` で cache を
 * 権威的に patch する (= list RPC を再度往復せずに済み、 更新結果が一覧に確実に
 * 反映される)。
 *
 * response に更新後 row が含まれない場合は invalidate にフォールバックする
 * (= stale な updatedAt で cache を patch すると、 次回の楽観ロック比較が失敗する)。
 */
export function useUpdateSingularDay(): InvalidatingMutationResultFor<
  typeof updateSingularDay
> {
  const queryClient = useQueryClient()
  return useMutation(updateSingularDay, {
    onSuccess: (data, variables) => {
      if (data.singularDay === undefined) {
        void queryClient.invalidateQueries({
          queryKey: singularDayQueryKeys.list,
        })
        return
      }
      const updated = data.singularDay
      queryClient.setQueriesData<ListSingularDaysResponse>(
        { queryKey: singularDayQueryKeys.list },
        (old) => {
          if (old === undefined) {
            return old
          }
          return {
            ...old,
            singularDays: old.singularDays.map((sd) =>
              sd.id === variables.id ? updated : sd,
            ),
          }
        },
      )
    },
  })
}
